export type ConsultationAttachmentType =
  | 'image'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'video'
  | 'audio';

export type ConsultationAttachmentsSchema = {
  allowDragDrop?: boolean;
  maxFileSizeBytes?: number;
  maxFiles?: number;
  allowedTypes?: ConsultationAttachmentType[];
};

export type ConsultationAttachmentItem = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  url: string;
  objectKey?: string;
  uploadedAtUtc?: string;
};

export type ConsultationAttachmentsValue = {
  attachments: ConsultationAttachmentItem[];
};

export const CONSULTATION_ATTACHMENT_TYPE_OPTIONS: Array<{
  value: ConsultationAttachmentType;
  label: string;
}> = [
  { value: 'image', label: 'Images' },
  { value: 'pdf', label: 'PDF' },
  { value: 'document', label: 'Documents' },
  { value: 'spreadsheet', label: 'Spreadsheets' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
];

export const DEFAULT_ATTACHMENTS_SCHEMA: ConsultationAttachmentsSchema = {
  allowDragDrop: true,
  maxFileSizeBytes: 10 * 1024 * 1024,
  maxFiles: 10,
  allowedTypes: ['image', 'pdf', 'document'],
};

export const DEFAULT_ATTACHMENTS_SCHEMA_JSON = JSON.stringify(DEFAULT_ATTACHMENTS_SCHEMA, null, 2);

const readJsonString = (source: Record<string, unknown>, ...keys: string[]): string => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string') {
      return value.trim();
    }

    if (value !== undefined && value !== null) {
      return String(value).trim();
    }
  }

  return '';
};

const readJsonNumber = (source: Record<string, unknown>, ...keys: string[]): number => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return 0;
};

const normalizeAttachmentItem = (raw: unknown): ConsultationAttachmentItem | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const id = readJsonString(item, 'id', 'Id');
  const fileName = readJsonString(item, 'fileName', 'FileName');
  const contentType = readJsonString(item, 'contentType', 'ContentType');
  const url = readJsonString(item, 'url', 'Url');

  if (!id || !fileName || !contentType || !url) {
    return null;
  }

  const objectKey = readJsonString(item, 'objectKey', 'ObjectKey');
  const uploadedAtUtc = readJsonString(item, 'uploadedAtUtc', 'UploadedAtUtc');

  return {
    id,
    fileName,
    contentType,
    sizeBytes: readJsonNumber(item, 'sizeBytes', 'SizeBytes'),
    url,
    ...(objectKey ? { objectKey } : {}),
    ...(uploadedAtUtc ? { uploadedAtUtc } : {}),
  };
};

export const parseConsultationAttachmentsSchema = (schemaJson?: string): ConsultationAttachmentsSchema | null => {
  if (!schemaJson?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(schemaJson) as Record<string, unknown>;
    const allowedTypes = (parsed.allowedTypes ?? parsed.AllowedTypes) as ConsultationAttachmentType[] | undefined;
    if (!Array.isArray(allowedTypes) || allowedTypes.length === 0) {
      return null;
    }

    return {
      allowDragDrop: (parsed.allowDragDrop ?? parsed.AllowDragDrop) as boolean | undefined,
      maxFileSizeBytes: (parsed.maxFileSizeBytes ?? parsed.MaxFileSizeBytes) as number | undefined,
      maxFiles: (parsed.maxFiles ?? parsed.MaxFiles) as number | undefined,
      allowedTypes,
    };
  } catch {
    return null;
  }
};

export const parseAttachmentsValue = (rawValue: string | undefined): ConsultationAttachmentsValue => {
  if (!rawValue?.trim()) {
    return { attachments: [] };
  }

  try {
    const parsed = JSON.parse(rawValue) as Record<string, unknown>;
    const rawAttachments = parsed.attachments ?? parsed.Attachments;
    if (!Array.isArray(rawAttachments)) {
      return { attachments: [] };
    }

    const attachments = rawAttachments
      .map(normalizeAttachmentItem)
      .filter((item): item is ConsultationAttachmentItem => item !== null);

    return { attachments };
  } catch {
    return { attachments: [] };
  }
};

export const serializeAttachmentsValue = (value: ConsultationAttachmentsValue): string =>
  JSON.stringify(value);

export const createEmptyAttachmentsValue = (): ConsultationAttachmentsValue => ({ attachments: [] });

export const validateConsultationAttachmentsValue = (
  schemaJson: string | undefined,
  rawValue: string | undefined,
): string | null => {
  const schema = parseConsultationAttachmentsSchema(schemaJson);
  if (!schema) {
    return 'field definition schemaJson is invalid';
  }

  if (!rawValue?.trim()) {
    return null;
  }

  const value = parseAttachmentsValue(rawValue);
  const maxFiles = schema.maxFiles ?? DEFAULT_ATTACHMENTS_SCHEMA.maxFiles ?? 10;
  if (value.attachments.length > maxFiles) {
    return `value must include at most ${maxFiles} attachment(s)`;
  }

  const maxFileSizeBytes = schema.maxFileSizeBytes ?? DEFAULT_ATTACHMENTS_SCHEMA.maxFileSizeBytes ?? 0;
  for (let index = 0; index < value.attachments.length; index += 1) {
    const attachment = value.attachments[index];
    if (!attachment.id || !attachment.fileName || !attachment.contentType || !attachment.url) {
      return `attachments[${index}] is missing required metadata`;
    }

    if (attachment.sizeBytes <= 0) {
      return `attachments[${index}].sizeBytes must be greater than zero`;
    }

    if (attachment.sizeBytes > maxFileSizeBytes) {
      return `attachments[${index}] exceeds configured max file size`;
    }

    if (!matchesAllowedTypes(schema.allowedTypes ?? [], attachment.contentType, attachment.fileName)) {
      return `attachments[${index}] content type is not allowed for this field`;
    }
  }

  return null;
};

export const hasConsultationAttachmentsValue = (
  schemaJson: string | undefined,
  rawValue: string | undefined,
): boolean => parseAttachmentsValue(rawValue).attachments.length > 0;

export const matchesAllowedType = (
  allowedType: ConsultationAttachmentType,
  contentType: string,
  fileName: string,
): boolean => {
  const extension = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.')).toLowerCase() : '';

  switch (allowedType) {
    case 'image':
      return contentType.startsWith('image/') || ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(extension);
    case 'pdf':
      return contentType === 'application/pdf' || extension === '.pdf';
    case 'document':
      return [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'application/rtf',
      ].includes(contentType) || ['.doc', '.docx', '.txt', '.rtf'].includes(extension);
    case 'spreadsheet':
      return [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
      ].includes(contentType) || ['.xls', '.xlsx', '.csv'].includes(extension);
    case 'video':
      return contentType.startsWith('video/') || ['.mp4', '.mov', '.avi', '.webm', '.mkv'].includes(extension);
    case 'audio':
      return contentType.startsWith('audio/') || ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'].includes(extension);
    default:
      return false;
  }
};

export const matchesAllowedTypes = (
  allowedTypes: ConsultationAttachmentType[],
  contentType: string,
  fileName: string,
): boolean => allowedTypes.some((allowedType) => matchesAllowedType(allowedType, contentType, fileName));

export const isImageAttachment = (contentType: string, fileName: string): boolean =>
  matchesAllowedType('image', contentType, fileName);

export const validateLocalAttachmentFile = (
  schema: ConsultationAttachmentsSchema,
  file: File,
  currentCount: number,
): string | null => {
  const maxFiles = schema.maxFiles ?? DEFAULT_ATTACHMENTS_SCHEMA.maxFiles ?? 10;
  if (currentCount >= maxFiles) {
    return `No more than ${maxFiles} attachment(s) are allowed`;
  }

  const maxFileSizeBytes = schema.maxFileSizeBytes ?? DEFAULT_ATTACHMENTS_SCHEMA.maxFileSizeBytes ?? 0;
  if (file.size > maxFileSizeBytes) {
    return `File exceeds the configured max size of ${Math.round(maxFileSizeBytes / (1024 * 1024))} MB`;
  }

  if (!matchesAllowedTypes(schema.allowedTypes ?? [], file.type || 'application/octet-stream', file.name)) {
    return 'File type is not allowed for this field';
  }

  return null;
};

export const buildAttachmentsAcceptAttribute = (allowedTypes: ConsultationAttachmentType[]): string => {
  const tokens = new Set<string>();
  allowedTypes.forEach((allowedType) => {
    switch (allowedType) {
      case 'image':
        tokens.add('image/*');
        break;
      case 'pdf':
        tokens.add('.pdf');
        tokens.add('application/pdf');
        break;
      case 'document':
        tokens.add('.doc');
        tokens.add('.docx');
        tokens.add('.txt');
        tokens.add('.rtf');
        break;
      case 'spreadsheet':
        tokens.add('.xls');
        tokens.add('.xlsx');
        tokens.add('.csv');
        break;
      case 'video':
        tokens.add('video/*');
        break;
      case 'audio':
        tokens.add('audio/*');
        break;
      default:
        break;
    }
  });

  return Array.from(tokens).join(',');
};

export const formatAttachmentSize = (sizeBytes: number): string => {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }

  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`;
  }

  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const attachmentsSchemaToEditorState = (schemaJson?: string) => {
  const schema = parseConsultationAttachmentsSchema(schemaJson) ?? DEFAULT_ATTACHMENTS_SCHEMA;
  return {
    allowDragDrop: schema.allowDragDrop ?? true,
    maxFileSizeMb: Math.max(1, Math.round((schema.maxFileSizeBytes ?? DEFAULT_ATTACHMENTS_SCHEMA.maxFileSizeBytes ?? 0) / (1024 * 1024))),
    maxFiles: schema.maxFiles ?? DEFAULT_ATTACHMENTS_SCHEMA.maxFiles ?? 10,
    allowedTypes: schema.allowedTypes ?? DEFAULT_ATTACHMENTS_SCHEMA.allowedTypes ?? [],
  };
};

export const editorStateToAttachmentsSchemaJson = (state: {
  allowDragDrop: boolean;
  maxFileSizeMb: number;
  maxFiles: number;
  allowedTypes: ConsultationAttachmentType[];
}): string => JSON.stringify({
  allowDragDrop: state.allowDragDrop,
  maxFileSizeBytes: Math.max(1, state.maxFileSizeMb) * 1024 * 1024,
  maxFiles: Math.max(1, state.maxFiles),
  allowedTypes: state.allowedTypes,
}, null, 2);
